import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  axApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

// Asset APIs
export const createAsset = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters("ax", "assets");
  const response = await axApiClient.post(url, payload);
  return response.data;
};

export const updateAsset = async (
  assetId: string,
  payload: any
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    `assets/${assetId}`
  );
  const response = await axApiClient.patch(url, payload);
  return response.data;
};

// Dropdown APIs
export const getFacilitiesList = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "facilities-info",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );

  const response = await axApiClient.get(url);
  return response.data;
};

export const getSystemNetworkList = async (
  facilityId: string
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "system-network-info",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      if (facilityId) {
        qs.push("facility_id", facilityId);
      }
    }
  );

  const response = await axApiClient.get(url);
  return response.data;
};

export const getAssetStatusList = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/asset_status"
  );
  const response = await axApiClient.get(url);
  return response.data;
};

export const getDepreciationMethodList = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "choices/depreciation_method"
  );
  const response = await axApiClient.get(url);
  return response.data;
};

export const getAssetClassList = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "asset_class");
      qs.push("show_inactive", true);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getAssetTypeList = async (code: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "asset_type");
      qs.push("code_list", code);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getManufacturer = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "manufacturer",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
      query.push("is_active", 1);
      query.push("is_all_data", 1);
    }
  );
  const response = await axApiClient.get(url);
  return response.data.results ?? [];
};

export const getAsset = async () => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "assets",
    (query) => {
      query.push("remote_utility_id", getRemoteUtilityId());
    }
  );
  const response = await axApiClient.get(url);
  return response.data.results ?? [];
};

export const getDimensionList = async (code: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("asset_type_characteristics", "dimensions");
      qs.push("code_list", code);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

export const getAssetList = async (
  queryParams?: Record<string, any>
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "assets",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            qs.push(key, value.toString());
          }
        });
      }
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

export const getAssetCondition = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/condition",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("show_inactive", "true");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

export const getAssetDetail = async (id: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithPathVariables("ax", "assets", [id]);
  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getPerformanceDetail = async (id: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    `assets/${id}/performance`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
      qs.push("date_filter", "monthly");
    }
  );
  const response = await axApiClient.get(url);
  return response.data.result;
};

export const getConditionAssessmentDetail = async (
  id: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    `assets/${id}/condition-assessment`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
    }
  );
  const response = await axApiClient.get(url);
  return response.data;
};

export const getAssetSummary = async (): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "assets-summary",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );

  const response = await axApiClient.get(url);
  return response.data;
};

export const getOAndM = async (id: string, search: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    "service-order",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("request_id", id);

      if (search) {
        qs.push("search_data", search);
      }
    }
  );

  const response = await axApiClient.get(url);
  return response.data;
};

export const deleteAsset = async (
  assetId: string,
  remoteUtilityId: string
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "ax",
    `assets/${assetId}`,
    (query) => {
      query.push("remote_utility_id", remoteUtilityId);
    }
  );

  const response = await axApiClient.delete(url);
  return response.data;
};
